import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { OrgAnalytics } from '../types/database'

type Period = 7 | 30 | 90

interface UseOrgAnalyticsReturn {
  analytics: OrgAnalytics | null
  loading: boolean
  error: string | null
  period: Period
  setPeriod: (period: Period) => void
  loadAnalytics: (organizationId: string) => Promise<void>
  refresh: () => Promise<void>
}

const defaultAnalytics: OrgAnalytics = {
  logins_by_day: [],
  logins_by_app: [],
  top_users: [],
  active_users_7d: 0,
  active_users_30d: 0,
  total_logins_period: 0,
  success_rate: 0
}

export function useOrgAnalytics(): UseOrgAnalyticsReturn {
  const [analytics, setAnalytics] = useState<OrgAnalytics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriodState] = useState<Period>(30)
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null)

  const loadAnalytics = useCallback(async (organizationId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentOrgId(organizationId)

      const { data, error: fetchError } = await supabase.rpc('get_org_analytics', {
        p_organization_id: organizationId,
        p_days: period
      })

      if (fetchError) throw fetchError

      setAnalytics(data || defaultAnalytics)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
      setAnalytics(defaultAnalytics)
    } finally {
      setLoading(false)
    }
  }, [period])

  const setPeriod = useCallback((newPeriod: Period) => {
    setPeriodState(newPeriod)
  }, [])

  const refresh = useCallback(async () => {
    if (currentOrgId) {
      await loadAnalytics(currentOrgId)
    }
  }, [currentOrgId, loadAnalytics])

  return {
    analytics,
    loading,
    error,
    period,
    setPeriod,
    loadAnalytics,
    refresh
  }
}
